import { useMemo } from 'react'
import { typography, ItemsList, Flex } from '@retailer-platform/shared-components/src/tds'
import styled from '@emotion/styled'
import { type NavMenuHierarchyL2 } from '../nav-v2/NavMenuHierarchy'
import { useDashIntl } from '../../../intl/intl.hooks'
import { usePermissionCalculations } from './hooks/usePermissionCalculations'
import { getPermissionCheckboxProps } from './utils/permissionCheckboxUtils'
import {
  PermissionCheckbox,
  PermissionDropdown,
  PermissionMarkers,
  RoleMarkers,
} from './components'
import { SPACING, COLUMN_LAYOUTS } from './constants'

const ColumnContent = styled.div({
  ...typography.bodyRegular,
  paddingRight: SPACING.MEDIUM,
})

interface ToolPermissionRowProps {
  /** The tool/menu item to render permissions for */
  tool: NavMenuHierarchyL2
  /** Set of currently selected permission slugs */
  selectedPermissionSlugs: Set<string>
  /** Callback to handle changes to selected permissions */
  handlePermissionSlugsChange: (newSelectedPermissionSlugs: Set<string>) => void
  /** Whether the component is in read-only mode */
  readOnly?: boolean
  /** Set of permissions assigned through roles */
  roleAssignedPermissionSlugs?: Set<string>
  /** Array of user roles with their permissions */
  userRoles?: { label: string; permissions: { slug: string }[] }[]
}

/**
 * Renders a row for tool permissions with checkbox, description, and access controls.
 * Supports both editable and read-only modes with optional role information.
 */
export const ToolPermissionRow = ({
  tool,
  selectedPermissionSlugs,
  handlePermissionSlugsChange,
  readOnly,
  roleAssignedPermissionSlugs,
  userRoles,
}: ToolPermissionRowProps) => {
  const dashIntl = useDashIntl()

  const i18nId = 'permissionAccordion.permissions.description.' + tool.name
  let i18nDescription = dashIntl.formatMessage({ id: i18nId as any })

  if (i18nId == i18nDescription) {
    i18nDescription = ' - '
  }

  const { toolRequiredPermissionSet, associatedRoles, toolHardEnabled } = usePermissionCalculations(
    tool,
    userRoles,
    roleAssignedPermissionSlugs
  )

  const { isChecked, isDisabled, onCheckboxChange } = getPermissionCheckboxProps({
    tool,
    selectedPermissionSlugs,
    toolRequiredPermissionSet,
    toolHardEnabled,
    handlePermissionSlugsChange,
  })

  const numberOfColumns = userRoles ? COLUMN_LAYOUTS.FOUR_COLUMNS : COLUMN_LAYOUTS.THREE_COLUMNS

  const rowWidth = useMemo(() => (100 / numberOfColumns).toFixed(2) + '%', [numberOfColumns])

  return (
    <ItemsList.Section data-testid={`tool-permission-row-${tool.name}`}>
      <Flex.Row gap={'24px'} alignItems="start">
        <Flex.Column css={{ width: rowWidth }}>
          <PermissionCheckbox
            tool={tool}
            isChecked={isChecked}
            isDisabled={isDisabled}
            onCheckboxChange={onCheckboxChange}
            readOnly={readOnly}
          />
        </Flex.Column>

        <Flex.Column css={{ width: rowWidth }}>
          <ColumnContent>{i18nDescription || ''}</ColumnContent>
        </Flex.Column>

        <Flex.Column css={{ width: rowWidth }}>
          {readOnly ? (
            <PermissionMarkers tool={tool} selectedPermissionSlugs={selectedPermissionSlugs} />
          ) : (
            <PermissionDropdown
              tool={tool}
              selectedPermissionSlugs={selectedPermissionSlugs}
              handlePermissionSlugsChange={handlePermissionSlugsChange}
              isChecked={isChecked}
              roleAssignedPermissionSlugs={roleAssignedPermissionSlugs}
            />
          )}
        </Flex.Column>

        {userRoles && (
          <Flex.Column css={{ width: rowWidth }}>
            <RoleMarkers associatedRoles={associatedRoles} />
          </Flex.Column>
        )}
      </Flex.Row>
    </ItemsList.Section>
  )
}
